/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useEffect, useRef } from 'react'
import * as THREE from 'three'
import { useFrame, useThree } from '@react-three/fiber'
import { cameraAt, fovAt } from './curve.ts'
import { beats } from './beats.ts'
import { frame } from './state.ts'
import { GROUND, WHITE } from '../palette.ts'

const pos = new THREE.Vector3()
const tgt = new THREE.Vector3()

export function FlightRig() {
  const { gl } = useThree()
  const pointer = useRef({ x: 0, y: 0, dx: 0, dy: 0 })

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      pointer.current.x = (e.clientX / globalThis.innerWidth) * 2 - 1
      pointer.current.y = (e.clientY / globalThis.innerHeight) * 2 - 1
    }
    globalThis.addEventListener('pointermove', onMove)
    return () => globalThis.removeEventListener('pointermove', onMove)
  }, [])

  useFrame((st, dt) => {
    const d = Math.min(dt, 0.1)
    // FOUNDATION §8 — never bind to raw scroll; critically damp, frame-rate independent.
    const target = frame.devP >= 0 ? frame.devP : frame.targetP
    frame.p += (target - frame.p) * (1 - Math.exp(-6 * d))
    const b = beats(frame.p)
    if (frame.devFlow >= 0) b.flow = frame.devFlow
    frame.beats = b

    const cam = st.camera as THREE.PerspectiveCamera
    cameraAt(frame.p, pos, tgt)
    cam.position.copy(pos)
    cam.lookAt(tgt)

    // Pointer parallax — max ±1.5°, heavily damped (FOUNDATION §7).
    const px = pointer.current
    px.dx += (px.x - px.dx) * (1 - Math.exp(-3 * d))
    px.dy += (px.y - px.dy) * (1 - Math.exp(-3 * d))
    cam.rotateY(-px.dx * 0.026)
    cam.rotateX(-px.dy * 0.026)

    cam.fov = fovAt(frame.p)
    cam.updateProjectionMatrix()

    frame.bg.copy(GROUND).lerp(WHITE, b.bgWhite)
    gl.setClearColor(frame.bg, 1)
  }, -100)

  return null
}
